/* SIDEBAR_COMPACTA_V1 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  NavLink,
  useLocation,
} from "react-router-dom";
import {
  ChevronDown,
  CircleHelp,
  Search,
  X,
  Headphones,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { MODULES } from "@/lib/modules";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const STORAGE_KEY = "simpler:sidebar:collapsed";

/**
 * Organização visual inspirada na navegação da Veri.
 *
 * Esta configuração não altera:
 * - rotas;
 * - permissões;
 * - catálogo MODULES;
 * - páginas;
 * - backend.
 *
 * Cards internos do Dashboard Federal não são exibidos aqui.
 */
export const MENU_STRUCTURE = [
  {
    key: "inicio",
    label: null,
    entries: [
      {
        type: "item",
        slug: "dashboard",
        label: "Início",
      },
    ],
  },
  {
    key: "meu-dia",
    label: "Meu dia",
    entries: [
      {
        type: "group",
        key: "gestao-vista",
        label: "Gestão à Vista 360°",
        slugs: [
          "painel-360",
          "bi-ausencias",
          "bi-diagnosticos",
          "bi",
        ],
      },
      {
        type: "item",
        slug: "prioridades",
        label: "Central de Prioridades",
      },
    ],
  },
  {
    key: "monitoramento",
    label: "Monitoramento",
    entries: [
      {
        type: "item",
        slug: "mensagens",
      },
      {
        type: "item",
        slug: "dashboard-federal",
      },
      {
        type: "item",
        slug: "simples-nacional-apuracao",
      },
      {
        type: "item",
        slug: "parcelamentos",
      },
      {
        type: "item",
        slug: "certidoes",
      },
      {
        type: "item",
        slug: "debitos",
      },
      {
        type: "item",
        slug: "pgfn",
      },
    ],
  },
  {
    key: "raio-x-fiscal",
    label: "Raio-X Fiscal",
    entries: [
      {
        type: "group",
        key: "analise-diagnostico",
        label: "Análise e diagnóstico",
        slugs: [
          "health-score",
          "pre-analise",
          "pos-analise",
          "reforma-tributaria",
          "diagnostico",
        ],
      },
      {
        type: "item",
        slug: "relatorios",
      },
    ],
  },
  {
    key: "relacionamento",
    label: "Relacionamento",
    entries: [
      {
        type: "item",
        slug: "ia",
      },
      {
        type: "item",
        slug: "clientes",
      },
      {
        type: "item",
        slug: "atendimento",
        label: "Central de Atendimento",
      },
    ],
  },
  {
    key: "automacao",
    label: "Automação",
    entries: [
      {
        type: "group",
        key: "rotinas-automaticas",
        label: "Rotinas Automáticas",
        slugs: [
          "automacoes",
          "integracoes",
          "integracoes-scheduler",
        ],
      },
      {
        type: "item",
        slug: "area-do-cliente",
      },
    ],
  },
  {
    key: "administracao",
    label: "Administração",
    entries: [
      {
        type: "group",
        key: "cadastros",
        label: "Cadastros",
        slugs: [
          "empresas",
          "procuradores",
          "usuarios",
          "gerenciar-empresas",
        ],
      },
      {
        type: "group",
        key: "notificacoes",
        label: "Notificações",
        slugs: [
          "alertas",
        ],
      },
      {
        type: "group",
        key: "configuracoes",
        label: "Configurações",
        slugs: [
          "configuracoes",
          "meu-plano",
          "perfis-permissoes",
          "serpro-caixa-postal",
        ],
      },
    ],
  },
  {
    key: "treinamento",
    label: "Treinamento",
    entries: [
      {
        type: "group",
        key: "treinamento",
        label: "Treinamento",
        slugs: [
          "primeiros-passos",
          "jornada-simpler",
        ],
      },
    ],
  }, // PP11
  {
    key: "plataforma",
    label: "Plataforma",
    entries: [
      {
        type: "item",
        slug: "plataforma-saude",
      },
      {
        type: "item",
        slug: "plataforma-tenants",
      },
      {
        type: "item",
        slug: "plataforma-atendimento",
      },
      {
        type: "item",
        slug: "plataforma-resultados",
      },
      {
        type: "item",
        slug: "plataforma-financeiro",
      },
      {
        type: "item",
        slug: "plataforma-serpro",
      },
      {
        type: "item",
        slug: "plataforma-serpro-usage",
      },
    ],
  },
];

function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

// H17: itens que apontam para uma aba ficam ativos só na aba certa
function __tabAwareActive(module, navActive) {
  if (typeof window === "undefined") {
    return navActive;
  }
  const rawPath = String(module?.path || module?.to || "");
  const [base, query] = rawPath.split("?");
  const basePath = (base || "").replace(/\/+$/, "");
  if (basePath !== "/app/health-score") {
    return navActive;
  }
  // H17c: a aba do item vem do campo tab ou do próprio link (?tab=...)
  const itemTab =
    module?.tab ||
    (query ? new URLSearchParams(query).get("tab") : null) ||
    "health";
  const current = window.location.pathname.replace(/\/+$/, "");
  if (current !== "/app/health-score") {
    return false;
  }
  const tab = new URLSearchParams(window.location.search).get("tab") || "health";
  return tab === itemTab;
}

function isModulePathActive(module, pathname) {
  if (!module?.path) {
    return false;
  }

  if (module.exact) {
    return pathname === module.path;
  }

  return (
    pathname === module.path ||
    pathname.startsWith(`${module.path}/`)
  );
}

// SIDEBAR_SEMPRE_RECOLHIDA_V1
// Abrir no passar do mouse e estado passageiro, nao preferencia.
// Gravar isso no navegador fazia a barra voltar aberta: ao trocar
// de aba com o ponteiro em cima dela o mouseleave nunca acontece,
// e quando acontece o fechamento e agendado para 220ms depois e
// cancelado na desmontagem antes de gravar. Entao a barra sempre
// comeca recolhida e nada e guardado.
function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // A sidebar continua funcionando sem localStorage.
    }
  }, []);

  // SIDEBAR_AUTO_COLLAPSE_V1
  // Ao sair da aba/janela (ou o ponteiro sair do navegador) e ao voltar,
  // a barra recolhe. Ela so abre de novo quando o mouse entrar nela.
  useEffect(() => {
    const collapse = () => setCollapsed(true);
    const onVisibility = () => collapse();
    const onMouseOut = (e) => {
      if (!e.relatedTarget && !e.toElement) {
        collapse();
      }
    };
    window.addEventListener("blur", collapse);
    window.addEventListener("focus", collapse);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      window.removeEventListener("blur", collapse);
      window.removeEventListener("focus", collapse);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  return [
    collapsed,
    setCollapsed,
  ];
}

// Menu exclusivo do platform_owner (out/2026) — itens achados pelo path em MODULES
const _platSlug = (p) => (MODULES.find((m) => m.path === p) || {}).slug;
const PLATFORM_MENU_STRUCTURE = [
  { key: "plat-visao", label: "Visão geral", entries: [
    { type: "item", slug: _platSlug("/app/plataforma-inicio"), label: "Início" },
    { type: "item", slug: _platSlug("/app/plataforma-tenants"), label: "Escritórios" },
    { type: "item", slug: _platSlug("/app/plataforma-ativacao"), label: "Ativação de clientes" }, // PP10
  ]},
  { key: "plat-negocio", label: "Negócio", entries: [
    { type: "group", key: "plat-financeiro", label: "Financeiro",
      slugs: [_platSlug("/app/plataforma-resultados"), _platSlug("/app/plataforma-financeiro")].filter(Boolean) },
    { type: "item", slug: _platSlug("/app/plataforma-atendimento"), label: "Atendimento" },
  ]},
  { key: "plat-serpro", label: "Serpro", entries: [
    { type: "item", slug: _platSlug("/app/plataforma/consumo-serpro"), label: "Consumo Serpro" },
    { type: "item", slug: _platSlug("/app/plataforma-serpro"), label: "Configuração das APIs" },
    { type: "item", slug: _platSlug("/app/plataforma-saude"), label: "Saúde da plataforma" },
  ]},
  { key: "plat-tecnico", label: "Técnico", entries: [
    { type: "group", key: "plat-sync", label: "Motor de sincronização",
      slugs: [_platSlug("/app/integracoes"), _platSlug("/app/integracoes/scheduler")].filter(Boolean) },
  ]},
  { key: "plat-comercial", label: "Comercial", entries: [
    { type: "item", slug: _platSlug("/commercial-portal"), label: "Portal Comercial" },
  ]},
];

function SidebarContent({
  collapsed,
  onNavigate,
}) {
  const location = useLocation();
  const { user } = useAuth();

  const {
    hasPermission,
    isModuleEnabled,
    loading,
  } = usePermissions();

  const isPlatformOwner =
    user?.role === "platform_owner";

  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState(
    () => new Set()
  );

  const visibleModules = useMemo(
    () =>
      MODULES.filter((module) => {
        if (
          isPlatformOwner &&
          !module.platformOnly
        ) {
          return false;
        }

        if (
          !isPlatformOwner &&
          module.platformOnly
        ) {
          return false;
        }

        // platform_owner vê todos os itens da plataforma, sem depender das permissões carregarem
        if (isPlatformOwner) {
          return true;
        }

        if (loading) {
          return true;
        }

        if (
          module.requiredPermission &&
          !hasPermission(
            module.requiredPermission
          )
        ) {
          return false;
        }

        if (
          module.moduleSlug &&
          module.requiredPermission &&
          !isModuleEnabled(
            module.moduleSlug
          )
        ) {
          return false;
        }

        return true;
      }),
    [
      hasPermission,
      isModuleEnabled,
      isPlatformOwner,
      loading,
    ]
  );

  const navigation = useMemo(() => {
    const modulesBySlug = new Map(
      visibleModules.map((module) => [
        module.slug,
        module,
      ])
    );

    return (isPlatformOwner ? PLATFORM_MENU_STRUCTURE : MENU_STRUCTURE).map((section) => {
      const entries = section.entries
        .map((entry) => {
          if (entry.type === "item") {
            const module =
              modulesBySlug.get(entry.slug);

            if (!module) {
              return null;
            }

            return {
              ...entry,
              key: `${section.key}:${entry.slug}`,
              module,
            };
          }

          const children = entry.slugs
            .map((slug) =>
              modulesBySlug.get(slug)
            )
            .filter(Boolean);

          if (children.length === 0) {
            return null;
          }

          return {
            ...entry,
            key: `${section.key}:${entry.key}`,
            children,
          };
        })
        .filter(Boolean);

      return {
        ...section,
        entries,
      };
    }).filter(
      (section) =>
        section.entries.length > 0
    );
  }, [isPlatformOwner, visibleModules]);

  const activeGroupKey = useMemo(() => {
    for (const section of navigation) {
      for (const entry of section.entries) {
        if (
          entry.type !== "group"
        ) {
          continue;
        }

        const hasActiveChild =
          entry.children.some((module) =>
            isModulePathActive(
              module,
              location.pathname
            )
          );

        if (hasActiveChild) {
          return entry.key;
        }
      }
    }

    return null;
  }, [
    location.pathname,
    navigation,
  ]);

  useEffect(() => {
    if (!activeGroupKey) {
      return;
    }

    setOpenGroups((current) => {
      const next = new Set(current);

      next.add(activeGroupKey);

      return next;
    });
  }, [activeGroupKey]);

  const normalizedSearch =
    normalizeText(search);

  const filteredNavigation = useMemo(() => {
    if (!normalizedSearch) {
      return navigation;
    }

    return navigation
      .map((section) => {
        const entries = section.entries
          .map((entry) => {
            if (
              entry.type === "item"
            ) {
              const searchable =
                normalizeText(
                  [
                    entry.label,
                    entry.module.label,
                    entry.module.description,
                  ]
                    .filter(Boolean)
                    .join(" ")
                );

              return searchable.includes(
                normalizedSearch
              )
                ? entry
                : null;
            }

            const groupMatches =
              normalizeText(
                entry.label
              ).includes(normalizedSearch);

            const children = groupMatches
              ? entry.children
              : entry.children.filter(
                  (module) => {
                    const searchable =
                      normalizeText(
                        [
                          module.label,
                          module.description,
                        ]
                          .filter(Boolean)
                          .join(" ")
                      );

                    return searchable.includes(
                      normalizedSearch
                    );
                  }
                );

            if (children.length === 0) {
              return null;
            }

            return {
              ...entry,
              children,
            };
          })
          .filter(Boolean);

        return {
          ...section,
          entries,
        };
      })
      .filter(
        (section) =>
          section.entries.length > 0
      );
  }, [
    navigation,
    normalizedSearch,
  ]);

  const toggleGroup = (groupKey) => {
    setOpenGroups((current) => {
      const next = new Set(current);

      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }

      return next;
    });
  };

  const renderBadge = (module) => {
  if (
    module.available ||
    module.slug === "clientes"
  ) {
    return null;
  }

    return (
      <span className="ml-auto shrink-0 rounded-md border border-white/15 bg-white/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-white/85">
        Em breve
      </span>
    );
  };

  const renderMainItem = (
    module,
    customLabel
  ) => {
    const Icon = module.icon;

    return (
      <NavLink
        key={module.slug}
        to={module.path}
        end={module.exact}
        aria-current={__tabAwareActive(module, true) ? "page" : "false"}
        onClick={onNavigate}
        data-testid={module.testId}
        title={
          collapsed
            ? customLabel || module.label
            : undefined
        }
        className={({ isActive: navActive }) => {
          const isActive = __tabAwareActive(module, navActive);
          return cn(
            "group relative flex min-h-[38px] w-full items-center rounded-xl font-semibold transition-all duration-150",
            collapsed
              ? "mx-auto h-11 w-11 justify-center px-0"
              : "gap-2 px-2.5 py-1.5 text-[12px]",
            isActive
              ? "bg-sidebar-active text-sidebar-active-foreground shadow-sm"
              : "bg-transparent text-sidebar-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground"
          );
        }}
      >
        <Icon
          className="h-[17px] w-[17px] shrink-0"
          strokeWidth={1.8}
        />

        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate leading-[16px]">
              {customLabel || module.label}
            </span>

            {renderBadge(module)}
          </>
        )}
      </NavLink>
    );
  };

  const renderChildItem = (module) => (
    <NavLink
      key={module.slug}
      to={module.path}
      end={module.exact}
      aria-current={__tabAwareActive(module, true) ? "page" : "false"}
      onClick={onNavigate}
      data-testid={module.testId}
      className={({ isActive: navActive }) => {
        const isActive = __tabAwareActive(module, navActive);
        return cn(
          "flex min-h-[34px] items-center whitespace-nowrap rounded-lg py-1.5 pl-[40px] pr-2.5 text-[12.5px] font-medium transition-all duration-150",
          isActive
            ? "bg-white/10 text-white"
            : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground"
        );
      }}
    >
      <span className="min-w-0 flex-1 truncate leading-[16px]">
        {module.label}
      </span>

      {renderBadge(module)}
    </NavLink>
  );

  return (
    <>
      <div
        className={cn(
          "shrink-0",
          collapsed
            ? "px-2 pb-2 pt-3"
            : "px-5 pb-3 pt-5"
        )}
      >
        <NavLink
          to="/app"
          onClick={onNavigate}
          data-testid="brand-home"
          title="Simpler"
          className={cn(
            "flex items-center bg-transparent hover:bg-transparent",
            collapsed
              ? "justify-center"
              : "gap-3"
          )}
        >
          <img
            src="/brand/simpler-logo-oficial.png"
            alt="Simpler"
            className={cn(
              "shrink-0 object-contain",
              collapsed
                ? "h-8 w-8"
                : "h-10 w-10"
            )}
          />

          {!collapsed && (
            <span className="text-[22px] font-semibold leading-none tracking-[-0.03em] text-white">
              Simpler
            </span>
          )}
        </NavLink>
      </div>

      {!collapsed && (
        <div className="shrink-0 px-3 pb-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-muted"
              strokeWidth={1.8}
            />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Buscar no menu..."
              aria-label="Buscar no menu"
              className="h-[35px] w-full rounded-lg border border-white/15 bg-white/5 py-2 pl-9 pr-9 text-xs text-sidebar-foreground outline-none transition placeholder:text-sidebar-muted focus:border-white/30 focus:bg-white/10"
            />

            {search && (
              <button
                type="button"
                onClick={() =>
                  setSearch("")
                }
                aria-label="Limpar busca"
                className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-sidebar-muted hover:bg-white/10 hover:text-sidebar-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <nav
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden pb-4",
          collapsed
            ? "space-y-2 px-2 sidebar-scrollbar-hidden"
            : "space-y-4 px-3 sidebar-scrollbar-thin"
        )}
        data-testid="sidebar-nav"
      >
        {filteredNavigation.length === 0 &&
          !collapsed && (
            <div className="px-3 py-8 text-center text-xs text-sidebar-muted">
              Nenhum item encontrado.
            </div>
          )}

        {filteredNavigation.map(
          (section, sectionIndex) => (
            <div
              key={section.key}
              data-testid={`nav-group-${section.key}`}
            >
              {!collapsed &&
                section.label && (
                  <div className="mb-1.5 px-3 pt-1 text-[9.5px] font-bold uppercase tracking-[0.04em] text-sidebar-foreground/95">
                    {section.label}
                  </div>
                )}

              {collapsed &&
                sectionIndex > 0 && (
                  <div className="mx-auto mb-2 h-px w-7 bg-sidebar-border" />
                )}

              <div className="space-y-px">
                {section.entries.map(
                  (entry) => {
                    if (
                      entry.type === "item"
                    ) {
                      return renderMainItem(
                        entry.module,
                        entry.label
                      );
                    }

                    const GroupIcon =
                      entry.children[0]?.icon;

                    const isOpen =
                      Boolean(
                        normalizedSearch
                      ) ||
                      openGroups.has(
                        entry.key
                      );

                    const isActive =
                      entry.key ===
                      activeGroupKey;

                    return (
                      <div key={entry.key}>
                        <button
                          type="button"
                          onClick={() =>
                            toggleGroup(
                              entry.key
                            )
                          }
                          aria-expanded={
                            isOpen
                          }
                          title={
                            collapsed
                              ? entry.label
                              : undefined
                          }
                          className={cn(
                            "group relative flex min-h-[38px] w-full items-center rounded-xl font-semibold transition-all duration-150",
                            collapsed
                              ? "mx-auto h-11 w-11 justify-center px-0"
                              : "gap-2 px-2.5 py-1.5 text-[12px]",
                            isActive
                              ? "text-sidebar-active-foreground"
                              : "text-sidebar-foreground hover:bg-sidebar-hover"
                          )}
                        >
                          {GroupIcon && (
                            <GroupIcon
                              className="h-[17px] w-[17px] shrink-0"
                              strokeWidth={
                                1.8
                              }
                            />
                          )}

                          {!collapsed && (
                            <>
                              <span className="min-w-0 flex-1 truncate text-left leading-[16px]">
                                {
                                  entry.label
                                }
                              </span>

                              <ChevronDown
                                className={cn(
                                  "h-3.5 w-3.5 shrink-0 text-sidebar-muted transition-transform duration-200",
                                  isOpen &&
                                    "rotate-180"
                                )}
                                strokeWidth={
                                  2
                                }
                              />
                            </>
                          )}
                        </button>

                        {!collapsed && (
                          <div
                            className={cn(
                              "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                              isOpen
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                            )}
                          >
                            <div className="overflow-hidden">
                              <div className="space-y-0.5 pb-1">
                                {entry.children.map(
                                  renderChildItem
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )
        )}
      {/* RODAPE_DENTRO_DA_LISTA_V1
          Antes era rodape fixo e roubava altura permanente.
          Agora e o ultimo item da lista, aparece ao rolar. */}
      <div className="mt-2">
        <NavLink
          to="/app/atendimento"
          onClick={onNavigate}
          title={
            collapsed
              ? "Fale Conosco"
              : undefined
          }
          data-testid="sidebar-footer-fale-conosco"
          className={({ isActive }) =>
            cn(
              "group relative flex min-h-[38px] w-full items-center rounded-xl font-semibold transition-all duration-150",
              collapsed
                ? "mx-auto h-11 w-11 justify-center px-0"
                : "gap-2 px-2.5 py-1.5 text-[12px]",
              isActive
                ? "bg-sidebar-active text-sidebar-active-foreground shadow-sm"
                : "bg-transparent text-sidebar-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground"
            )
          }
        >
          <CircleHelp
            className="h-[17px] w-[17px] shrink-0"
            strokeWidth={1.8}
          />

          {!collapsed && (
            <span className="truncate">
              Fale Conosco
            </span>
          )}
        </NavLink>
      </div>
      </nav>
    </>
  );
}

export function Sidebar() {
  const [
    collapsed,
    setCollapsed,
  ] = useSidebarCollapsed();

  const collapseTimerRef =
    useRef(null);

  const expandSidebar = () => {
    if (collapseTimerRef.current) {
      clearTimeout(
        collapseTimerRef.current
      );
    }

    setCollapsed(false);
  };

  const scheduleCollapse = () => {
    if (collapseTimerRef.current) {
      clearTimeout(
        collapseTimerRef.current
      );
    }

    collapseTimerRef.current =
      setTimeout(() => {
        setCollapsed(true);
      }, 220);
  };

  useEffect(
    () => () => {
      if (
        collapseTimerRef.current
      ) {
        clearTimeout(
          collapseTimerRef.current
        );
      }
    },
    []
  );

  useEffect(() => {
    document.documentElement.dataset.sidebar =
      collapsed
        ? "collapsed"
        : "expanded";
  }, [collapsed]);

  return (
    <aside
      data-testid="sidebar-space"
      className="relative hidden h-screen w-[64px] shrink-0 lg:block"
    >
      <div
        data-testid="sidebar"
        data-collapsed={collapsed}
        onMouseEnter={expandSidebar}
        onMouseLeave={scheduleCollapse}
        onFocusCapture={expandSidebar}
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden",
          "bg-sidebar text-sidebar-foreground",
          "transition-[width,box-shadow] duration-200 ease-out",
          collapsed
            ? "w-[64px] shadow-none"
            : "w-[220px] shadow-xl"
        )}
      >
        <SidebarContent
          collapsed={collapsed}
        />
      </div>
    </aside>
  );
}

export function MobileSidebar({
  open,
  setOpen,
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={setOpen}
    >
      <SheetContent
        side="left"
        data-testid="mobile-sidebar"
        className="flex w-[86vw] max-w-[300px] flex-col bg-sidebar p-0 text-sidebar-foreground"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>
            Menu
          </SheetTitle>

          <SheetDescription>
            Navegação principal do Simpler
            Soluções Fiscais
          </SheetDescription>
        </SheetHeader>

        <SidebarContent
          collapsed={false}
          onNavigate={() =>
            setOpen(false)
          }
        />
      </SheetContent>
    </Sheet>
  );
}
